const getParsedConversionThresholds = (primaryLedger, order) => {
  const { currentBalance, conversionThresholds, ledgerEntries } = primaryLedger;

  const allThresholds = [];
  const appliedThresholds = [];
  const unlockedThresholds = [];
  let nextThreshold = null;

  [...conversionThresholds]
    .sort((a, b) => a.cost - b.cost)
    .forEach((threshold) => {
      const { rewardOffer } = threshold;

      const couponImage = rewardOffer.coupons.find(coupon => coupon.channelName === 'loyaltyRewardTier');

      const couponDetailsImage = rewardOffer.coupons.find(coupon => coupon.channelName === 'app');

      const couponDetailsImageUrl = couponDetailsImage?.defaultImagePath || null;

      const couponImageUrl = couponImage?.defaultImagePath || couponDetailsImageUrl;

      const appliedOffer = order.offers.find(o => (
        o.loyaltyConversionThresholdPublicId === threshold.publicId
        && o.applicationStatus === 'Applied'
      ));

      const convertedThreshold = {
        ...threshold,
        rewardOffer: {
          ...rewardOffer,
          ...appliedOffer && { appliedOffer },
          cost: threshold.cost,
          couponImageUrl,
          couponDetailsImageUrl,
          loyaltyConversionThresholdPublicId: threshold.publicId
        }
      };

      allThresholds.push(convertedThreshold);

      if (convertedThreshold.rewardOffer.appliedOffer) appliedThresholds.push(convertedThreshold);

      if (convertedThreshold.isManuallyRunnable) unlockedThresholds.push(convertedThreshold);

      if (convertedThreshold.cost > currentBalance && nextThreshold === null) nextThreshold = convertedThreshold;
    });

  const isMultiTiered = allThresholds.length > 1;
  const finalThreshold = allThresholds[allThresholds.length - 1];
  const bestUnlockedThreshold = unlockedThresholds.length > 0 ? unlockedThresholds[unlockedThresholds.length - 1] : null;
  const pointsUntilNextReward = nextThreshold ? nextThreshold.cost - currentBalance : 0;

  return {
    allThresholds,
    appliedThresholds,
    bestUnlockedThreshold,
    currentBalance,
    finalThreshold,
    isMultiTiered,
    ledgerEntries,
    pointsUntilNextReward,
    unlockedThresholds
  };
};

const getParsedChallengeConversionThresholds = (challengeLedger, order) => {
  const { currentBalance, conversionThresholds, ledgerEntries, currency } = challengeLedger;

  const allChallengeThresholds = [];
  const appliedChallengeThresholds = [];
  const unlockedChallengeThresholds = [];
  let nextChallengeThreshold = null;

  [...conversionThresholds]
    .sort((a, b) => a.cost - b.cost)
    .forEach((threshold) => {
      let convertedThreshold = {
        ...threshold,
        cost: threshold.cost,
        loyaltyConversionThresholdPublicId: threshold.publicId
      };

      // Handle different conversion types
      if (threshold.isOfferBasedConversion && threshold.rewardOffer) {
        const { rewardOffer } = threshold;

        const couponImage = rewardOffer.coupons?.find(coupon => coupon.channelName === 'loyaltyRewardTier');
        const couponDetailsImage = rewardOffer.coupons?.find(coupon => coupon.channelName === 'app');
        const couponDetailsImageUrl = couponDetailsImage?.defaultImagePath || null;
        const couponImageUrl = couponImage?.defaultImagePath || couponDetailsImageUrl;

        const appliedOffer = order.offers.find(o => (
          o.loyaltyConversionThresholdPublicId === threshold.publicId
          && o.applicationStatus === 'Applied'
        ));

        convertedThreshold = {
          ...convertedThreshold,
          rewardOffer: {
            ...rewardOffer,
            ...appliedOffer && { appliedOffer },
            cost: threshold.cost,
            couponImageUrl,
            couponDetailsImageUrl,
            loyaltyConversionThresholdPublicId: threshold.publicId
          }
        };

        if (appliedOffer) appliedChallengeThresholds.push(convertedThreshold);
      }
      else if (threshold.isGiftCardBasedConversion) {
        // Handle gift card conversions
        convertedThreshold = {
          ...convertedThreshold,
          giftCardReward: {
            amount: threshold.giftCardAwardAmount,
            cost: threshold.cost,
            loyaltyConversionThresholdPublicId: threshold.publicId
          }
        };
      }
      else if (threshold.isPointBasedConversion) {
        // Handle point-based conversions
        convertedThreshold = {
          ...convertedThreshold,
          pointReward: {
            cost: threshold.cost,
            loyaltyConversionThresholdPublicId: threshold.publicId
          }
        };
      }

      allChallengeThresholds.push(convertedThreshold);

      if (convertedThreshold.isManuallyRunnable) unlockedChallengeThresholds.push(convertedThreshold);

      if (convertedThreshold.cost > currentBalance && nextChallengeThreshold === null) {
        nextChallengeThreshold = convertedThreshold;
      }
    });

  const isMultiTieredChallenge = allChallengeThresholds.length > 1;
  const finalChallengeThreshold = allChallengeThresholds[allChallengeThresholds.length - 1];
  const bestUnlockedChallengeThreshold = unlockedChallengeThresholds.length > 0
    ? unlockedChallengeThresholds[unlockedChallengeThresholds.length - 1]
    : null;
  const pointsUntilNextChallengeReward = nextChallengeThreshold
    ? nextChallengeThreshold.cost - currentBalance
    : 0;

  return {
    ledgerPublicId: challengeLedger.ledgerPublicId,
    currency: {
      ...currency,
      pluralNameCapitalCase: currency.pluralName.charAt(0).toUpperCase() + currency.pluralName.slice(1),
      pluralShortNameUpperCase: currency.pluralShortName.toUpperCase()
    },
    currentBalance,
    ledgerEntries,
    challengeStartDate: currency.startDate,
    challengeEndDate: currency.endDate,
    challengeProcessingType: currency.challengeProcessingType,
    conversionSchedules: currency.conversionSchedules,
    nextScheduledConversionDateTimeOffset: currency.nextScheduledConversionDateTimeOffset,
    allChallengeThresholds,
    appliedChallengeThresholds,
    unlockedChallengeThresholds,
    bestUnlockedChallengeThreshold,
    finalChallengeThreshold,
    isMultiTieredChallenge,
    pointsUntilNextChallengeReward,
    nextChallengeThreshold
  };
};


const getLedgerCurrency = ledgerCurrency => ({
  currency: {
    ...ledgerCurrency,
    pluralNameCapitalCase: ledgerCurrency.pluralName.charAt(0).toUpperCase() + ledgerCurrency.pluralName.slice(1),
    pluralShortNameUpperCase: ledgerCurrency.pluralShortName.toUpperCase()
  }
});

const getPrimaryLegder = (ledgers) => {
  const primaryLedger = ledgers.find(ledger => ledger.currency.isPrimaryCategory);

  return {
    currency: null,
    conversionThresholds: [],
    ...primaryLedger && { ...primaryLedger }
  };
};

const getChallengeLegders = ledgers => ledgers.filter(ledger => ledger.currency && ledger.currency.isChallengeCategory).map(challengeLedger => ({
  currency: null,
  conversionThresholds: [],
  ...challengeLedger && { ...challengeLedger }
}));


const getLoyaltyConfig = (params) => {
  const { tier, tierSummaryContent, referralLink } = params.loyalty;
  const { friendlyUriName } = params.merchant;
  const primaryLedger = getPrimaryLegder(params.loyalty.ledgers);
  const challengeLedgers = getChallengeLegders(params.loyalty.ledgers);
  const { conversionThresholds, currency } = primaryLedger;

  if (tier && tierSummaryContent) {
    Object.assign(tier, {
      title: tierSummaryContent.title,
      description: tierSummaryContent.metadata.hasNextTier ? tierSummaryContent.metadata.message : tierSummaryContent.metadata.messageNoNextTier
    });
  }

  // Process challenge ledgers with conversion thresholds
  const parsedChallengeLedgers = challengeLedgers
    .filter(ledger => ledger.conversionThresholds && ledger.conversionThresholds.length > 0)
    .map(ledger => getParsedChallengeConversionThresholds(ledger, params.order));

  return {
    ...params.loyalty,
    ...conversionThresholds.length > 0 && getParsedConversionThresholds(primaryLedger, params.order),
    ...currency && getLedgerCurrency(currency),
    challengeLedgers: parsedChallengeLedgers,
    isMembershipTierStatus: params.loyalty.profileSummaryDisplayFormat === 'MembershipTier',
    checkinToken: params.loyalty.checkinToken ?? null,
    tier,
    referralLink: referralLink
      || `${window.location.origin}/${friendlyUriName}/order/menu?enrollInLoyalty&utm_source=referral-link`
  };
};


const parseLoyalty = (params) => {
  const defaultLoyaltyConfig = {
    allThresholds: [],
    appliedThresholds: [],
    bestUnlockedThreshold: null,
    challengeLedgers: [],
    currentBalance: 0,
    currency: {
      name: 'point',
      pluralName: 'points',
      pluralNameCapitalCase: 'Points',
      pluralShortName: 'pts',
      pluralShortNameUpperCase: 'PTS',
      shortName: 'pt'
    },
    finalThreshold: null,
    isEnrolled: params.isEnrolled,
    isMembershipTierStatus: false,
    isMultiTiered: false,
    ledgerEntries: [],
    pointsUntilNextReward: 0,
    tier: null,
    unlockedThresholds: []
  };

  return {
    ...defaultLoyaltyConfig,
    ...params.loyalty && getLoyaltyConfig(params)
  };
};

export default parseLoyalty;
